import { memo, useCallback, useMemo } from 'react'
import isSameDay from 'date-fns/isSameDay'

import Separator from 'components/common/Separator/Component'
import { useTranslation, dateFnsLocales } from 'services/Translation'
import { primary } from 'styles/Colors'

import { Container, DayLine, DayTitle, NumbersGrid, Number, Event, Divider } from './Styles'
import { getNumberList, getTimeOffsForDay, getAvailableTimes, getNumberWeekList } from './Tools'

const Month = ({
  className,
  currDate,
  onDateChange,
  selectedDate,
  selectedDates = [],
  isRangeSelection = false,
  monthView: { month, year },
  small = false,
  mini = false,
  events = [],
  appointmentDuration = 0,
  disableBefore,
  dataCy = '',
  calendarType,
}) => {
  const { lang, formatDate: format } = useTranslation()

  const days = useMemo(() => {
    const out = []
    for (let i = 1; i < 7; i++) {
      const dayAbbr = dateFnsLocales[lang]?.localize.day(i, { width: 'abbreviated' })
      // Remove one letter from abbreviation (keep first 2 characters)
      out.push(dayAbbr?.slice(0, 2) || dayAbbr)
    }
    const sundayAbbr = dateFnsLocales[lang]?.localize.day(0, { width: 'abbreviated' })
    // Remove one letter from abbreviation (keep first 2 characters)
    out.push(sundayAbbr?.slice(0, 2) || sundayAbbr)
    return out
  }, [lang])

  const numberList = useMemo(() => {
    if (calendarType === 'week') return getNumberWeekList(year, month, days, format)
    return getNumberList(year, month, days, disableBefore, format)
  }, [calendarType, days, disableBefore, format, month, year])

  const handleNumberClick = useCallback(
    newDate => () => {
      onDateChange(new Date(year, month, newDate))
    },
    [month, onDateChange, year]
  )
  const getDayStatus = useCallback(
    date => {
      // 8 to 18
      const eventsOfTheDay = events.filter(
        e => !e.repeat?.frequency && isSameDay(new Date(e.time), date)
      )
      if (!eventsOfTheDay.length) return 'empty' // Todo: Warning this leads to a bug. return empty only if there are free slots. Otherwise it could be vacations.
      const timeOffsOfTheDay = getTimeOffsForDay(date, events)
      const availableTimes = getAvailableTimes([...eventsOfTheDay, ...timeOffsOfTheDay], date)
      if (!availableTimes.length) return 'full'
      if (availableTimes.find(t => t.duration >= appointmentDuration)) return 'filling'
      return 'full'
    },
    [events, appointmentDuration]
  )

  return (
    <Container small={small} className={className} mini={mini}>
      <DayLine className="mb-5" mini={mini}>
        {days.map(el => (
          <DayTitle key={el} small={small} mini={mini}>
            {`${el?.[0]?.toUpperCase()}${el.substring(1)}`}
          </DayTitle>
        ))}
      </DayLine>
      <NumbersGrid small={small} mini={mini}>
        {numberList.map(({ value, disabled, isOtherMonth, fullDate }, i) => {
          // Use fullDate if available, otherwise construct from current month view
          const loopDate = fullDate || new Date(year, month, value)
          
          const isToday =
            !disabled &&
            !isOtherMonth &&
            currDate.getDate() === value &&
            currDate.getMonth() === month &&
            currDate.getFullYear() === year

          let color = 'transparent'
          let colorEvent = 'transparent'
          const statusDay = !disabled ? getDayStatus(loopDate) : 'disabled'
          
          // Check if this date is selected (either single selection or in range)
          const isSelected = selectedDate ? isSameDay(loopDate, selectedDate) : 
            selectedDates.some(date => isSameDay(loopDate, date))
          
          // Check if this date is in a range (between two selected dates)
          const isInRange = isRangeSelection && selectedDates.length === 2 && 
            loopDate > selectedDates[0] && loopDate < selectedDates[1]
          
          if (!disabled) {
            if (isSelected) {
              color = '#EB7C0C' // Selected dates - orange
            } else if (isInRange) {
              color = '#EB7C0C50' // Semi-transparent for range
            } else if (isToday) {
              color = 'transparent' // Today - no background circle
            } else {
              switch (statusDay) {
                case 'filling':
                  // Only show dot indicator, not full background
                  color = 'transparent'
                  colorEvent = '#EB7C0C'
                  break
                case 'full':
                  color = '#F43E51'
                  break
              }
            }
          }

          // Handler that uses fullDate for cross-month selection
          const handleClick = disabled ? undefined : () => onDateChange(loopDate)

          return (
            <Number
              key={i}
              disabled={disabled}
              color={small || (!small && isToday) ? color : color}
              isToday={isToday}
              isOtherMonth={isOtherMonth}
              onClick={handleClick}
              small={small}
              mini={mini}
              data-cy={`${dataCy}_number_${month}_${value}`}
            >
              {statusDay === 'filling' && <Event color={colorEvent} mini={mini} />}
              {isToday && <Event color="#9B2A3B" isToday mini={mini} />}
              {value}
            </Number>
          )
        })}
      </NumbersGrid>
      {!small ||
        (!mini && (
          <>
            <Separator size="4vh" />
            <Divider />
          </>
        ))}
    </Container>
  )
}

Month.whyDidYouRender = true

export default memo(Month)
